import MeterBar from '@/components/console/meter-bar';
import ModeBadge from '@/components/console/mode-badge';
import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatBytes, formatDate } from '@/lib/format';
import AttachmentUpload from '@/pages/apps/components/attachment-upload';
import RemoveAttachment from '@/pages/apps/components/remove-attachment';
import { edit_app_list_record_path } from '@/rails/routes';
import type { AppShell, Attachment, ProviderMode } from '@/types/platform';
import { Link } from '@inertiajs/react';

interface AttachmentRow extends Attachment {
  recordTitle: string | null;
  listKey: string | null;
}

interface AttachmentsIndexProps {
  app: AppShell;
  attachments: AttachmentRow[];
  // Where the files are kept. Part of the props, never shown to the owner.
  bucket: string | null;
  storage: { used: number; limit: number };
  filesMode: ProviderMode;
}

const KINDS: [prefix: string, label: string][] = [
  ['image/', 'Image'],
  ['video/', 'Video'],
  ['audio/', 'Audio'],
  ['text/', 'Text'],
  ['application/pdf', 'PDF'],
];

// A file's type in a word, instead of its technical content type.
function fileKind(contentType: string | null): string {
  const match = KINDS.find(([prefix]) => (contentType ?? '').startsWith(prefix));

  return match ? match[1] : 'File';
}

const AttachmentsIndex = ({ app, attachments, storage, filesMode }: AttachmentsIndexProps) => {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Attachments"
        description="Every photo and file added to this app, newest first."
        badges={<ModeBadge mode={filesMode} />}
      />

      {filesMode === 'demo' ? (
        <p className="text-muted-foreground text-sm">Demo mode keeps files on this server.</p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="min-w-0 lg:col-span-2">
          <CardContent>
            {attachments.length === 0 ? (
              <EmptyState
                title="No attachments yet"
                description="Add photos and files on a record, or upload a file here."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Record</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead>
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attachments.map((attachment) => (
                    <TableRow key={attachment.id}>
                      <TableCell className="max-w-64 truncate font-medium">
                        {attachment.filename}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {formatBytes(attachment.byteSize)}
                      </TableCell>
                      <TableCell>{fileKind(attachment.contentType)}</TableCell>
                      <TableCell className="max-w-48 truncate">
                        {attachment.recordId && attachment.listKey ? (
                          <Link
                            href={edit_app_list_record_path(
                              app.id,
                              attachment.listKey,
                              attachment.recordId
                            )}
                            className="underline"
                          >
                            {attachment.recordTitle ?? 'Untitled'}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(attachment.createdAt)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noreferrer"
                            className="px-2 text-sm font-medium underline"
                            aria-label={`Open ${attachment.filename}`}
                          >
                            Open
                          </a>
                          <RemoveAttachment appId={app.id} attachment={attachment} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>
                <h2>Upload a file</h2>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <AttachmentUpload
                appId={app.id}
                inputId="attachment-file"
                label="File"
                provisioned={app.provisioned}
              />
              <p className="text-muted-foreground text-xs">
                A file uploaded here is not tied to a record.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>
                <h2>Space used</h2>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <MeterBar
                row={{
                  key: 'files',
                  label: 'Attachments',
                  used: storage.used,
                  limit: storage.limit,
                  unit: 'bytes',
                }}
              />
              <p className="text-muted-foreground text-xs">Counted across all your apps.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AttachmentsIndex;
